import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { callApi, HttpMethod } from "src/services/apiService";
import {
  IReassignedStats,
  ITrendItem,
} from "src/interfaces/Reports/IReassignedDashboard";
import StatCard from "src/pages/LogoCall/StatCard";
import { CHART_COLORS } from "src/pages/LogoCall/ChartColors";
import styles from "./ReassignedDashboard.module.css";

const METHOD_OPTIONS = [
  { value: "", label: "All Methods" },
  { value: "a", label: "API" },
  { value: "w", label: "Web Upload" },
  { value: "f", label: "SFTP" },
];

const PRODUCT_OPTIONS = [
  { value: "", label: "All Products" },
  { value: "1", label: "Basic RND" },
  { value: "2", label: "Enhanced RND" },
  { value: "3", label: "TCPA Authority" },
];

const ReassignedDashboard: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<IReassignedStats | null>(null);
  const [trendData, setTrendData] = useState<ITrendItem[]>([]);

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    new Date(new Date().setMonth(new Date().getMonth() - 1)),
    new Date(),
  ]);
  const [startDate, endDate] = dateRange;

  const [methodFilter, setMethodFilter] = useState("");
  const [productFilter, setProductFilter] = useState("");

  // Fetch stats (includes method + product breakdowns in one call)
  useEffect(() => {
    if (!startDate || !endDate) return;
    setIsLoading(true);
    const s = Math.floor(startDate.getTime() / 1000);
    const e = Math.floor(endDate.getTime() / 1000);

    callApi(`reassigned/dashboard?startDate=${s}&endDate=${e}`, HttpMethod.GET)
      .then((data) => setStats(data))
      .catch((err) => console.error("Failed to fetch stats:", err))
      .finally(() => setIsLoading(false));
  }, [startDate, endDate]);

  // Fetch trend data when date range or filters change
  useEffect(() => {
    if (!startDate || !endDate) return;
    const s = Math.floor(startDate.getTime() / 1000);
    const e = Math.floor(endDate.getTime() / 1000);
    const params = new URLSearchParams({
      startDate: String(s),
      endDate: String(e),
    });
    if (methodFilter) params.set("method", methodFilter);
    if (productFilter) params.set("productId", productFilter);

    callApi(`reassigned/dashboard/trend?${params}`, HttpMethod.GET)
      .then((data) => setTrendData(data ?? []))
      .catch((err) => console.error("Failed to fetch trend:", err));
  }, [startDate, endDate, methodFilter, productFilter]);

  if (isLoading) {
    return (
      <div className="p-3">
        <h2>Reassigned Report</h2>
        <div className="d-flex flex-column align-items-center justify-content-center mt-5">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">
            Loading reassigned report data. This may take a moment for large
            accounts.
          </p>
        </div>
      </div>
    );
  }

  const pieData = stats
    ? [
        { name: "Reassigned", value: stats.totalReassigned },
        { name: "Clean", value: stats.totalClean },
      ]
    : [];

  // Convert byMethod/byProduct from stats (BreakdownItem with value) to bar chart format
  const methodBreakdown = stats?.byMethod ?? [];
  const productBreakdown = stats?.byProduct ?? [];

  return (
    <div className="p-3">
      <h2>Reassigned Report</h2>

      {/* Filters Row */}
      <div className="d-flex align-items-center gap-3 mb-4">
        <div className={styles.datePickerWrapper}>
          <DatePicker
            selectsRange={true}
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => setDateRange(update)}
            className={`form-control ${styles.datePicker}`}
            placeholderText="Select date range"
            dateFormat="MMM d, yyyy"
          />
          <i className={`bi bi-calendar3 ${styles.calendarIcon}`}></i>
        </div>
        <select
          className={`form-select ${styles.filterSelect}`}
          value={methodFilter}
          onChange={(e) => setMethodFilter(e.target.value)}
        >
          {METHOD_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          className={`form-select ${styles.filterSelect}`}
          value={productFilter}
          onChange={(e) => setProductFilter(e.target.value)}
        >
          {PRODUCT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Stat Cards */}
      {stats && (
        <div className="row g-4 mb-4">
          <StatCard
            title="Total Records Submitted"
            current={stats.totalRecords}
            previous={null}
          />
          <div className="col-md-3">
            <div className="card h-100 border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex flex-column">
                  <div className="text-muted small">Total Reassigned</div>
                  <div className="d-flex align-items-center mt-2">
                    <h3 className="mb-0" style={{ color: CHART_COLORS.DANGER }}>
                      {stats.totalReassigned.toLocaleString()}
                    </h3>
                  </div>
                  <div className="text-muted small mt-1">
                    {stats.reassignedRate}% of total
                  </div>
                </div>
              </div>
            </div>
          </div>
          <StatCard
            title="Total Clean"
            current={stats.totalClean}
            previous={null}
          />
          <div className="col-md-3">
            <div className="card h-100 border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex flex-column">
                  <div className="text-muted small">Reassigned Rate</div>
                  <div className="d-flex align-items-center mt-2">
                    <h3 className="mb-0" style={{ color: CHART_COLORS.DANGER }}>
                      {stats.reassignedRate}%
                    </h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="row g-4">
        {/* Trend Line Chart */}
        <div className="col-md-8">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="mb-4">Submissions Over Time</h5>
              <div style={{ height: "400px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={trendData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="totalRecords"
                      name="Total Records"
                      stroke={CHART_COLORS.PRIMARY}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 6 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="totalReassigned"
                      name="Reassigned"
                      stroke={CHART_COLORS.DANGER}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Pie Chart — Reassigned vs Clean */}
        <div className="col-md-4">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="mb-4">Reassigned vs Clean</h5>
              <div style={{ height: "400px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={80}
                      outerRadius={120}
                      fill={CHART_COLORS.PRIMARY}
                      paddingAngle={5}
                      dataKey="value"
                      label
                    >
                      <Cell fill={CHART_COLORS.DANGER} />
                      <Cell fill={CHART_COLORS.SUCCESS} />
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Bar Chart — Volume by Method */}
        <div className="col-md-6">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="mb-4">Volume by Submission Method</h5>
              <div style={{ height: "350px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={methodBreakdown}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="value"
                      name="Total Records"
                      fill={CHART_COLORS.PRIMARY}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Bar Chart — Volume by Product */}
        <div className="col-md-6">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="mb-4">Volume by Product Type</h5>
              <div style={{ height: "350px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={productBreakdown}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="value"
                      name="Total Records"
                      fill={CHART_COLORS.SUCCESS}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReassignedDashboard;
